import * as Y from 'yjs';
import { base64ToUint8Array } from '../../utils/yjs';
import { Logger } from '@/utils/logger';

const logger = Logger.getLoggerWithPrefix('YjsTabSync');

// M4: Warn at 1MB, hard block at 10MB. BroadcastChannel uses structured clone
// with no strict size limit, but extremely large messages can cause jank.
const BROADCAST_WARN_SIZE = 1_000_000;  // 1MB
const BROADCAST_MAX_SIZE = 10_000_000;  // 10MB

interface YjsSyncMessage {
  type: 'yjs-update' | 'yjs-resync-needed';
  diagramId: string;
  updateBase64: string;
  seq: number | null;
  tabId: string;
}

export interface YjsTabSyncOptions {
  // Called when a remote tab's confirmed seq is received (M5)
  onRemoteSeq?: (seq: number) => void;
  // Called when a remote tab signals that a resync is needed (oversized broadcast)
  onResyncNeeded?: () => void;
}

export class YjsTabSync {
  private channel: BroadcastChannel | null = null;
  private tabId: string;
  private diagramId: string;
  private doc: Y.Doc;
  private options: YjsTabSyncOptions;

  constructor(diagramId: string, doc: Y.Doc, options?: YjsTabSyncOptions) {
    this.diagramId = diagramId;
    this.doc = doc;
    this.options = options ?? {};
    this.tabId = `tab_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;

    if (typeof window === 'undefined' || !('BroadcastChannel' in window)) {
      logger.info('BroadcastChannel not available');
      return;
    }

    // L4: Per-diagram channel name to avoid cross-diagram message noise
    this.channel = new BroadcastChannel(`creately-yjs-sync-${diagramId}`);
    this.channel.onmessage = this.handleMessage.bind(this);
    logger.info('Initialized for tab:', this.tabId);
  }

  broadcastUpdate(updateBase64: string, seq: number | null): void {
    if (!this.channel) {
      logger.warn('broadcastUpdate called but channel is null');
      return;
    }
    logger.info('📡 Broadcasting update', { seq, size: updateBase64.length, tabId: this.tabId, diagramId: this.diagramId });
    if (updateBase64.length > BROADCAST_MAX_SIZE) {
      // M4: Too large even for BroadcastChannel — signal other tabs to resync
      logger.warn('Broadcast too large, sending resync signal', { size: updateBase64.length });
      try {
        this.channel.postMessage({
          type: 'yjs-resync-needed',
          diagramId: this.diagramId,
          updateBase64: '',
          seq,
          tabId: this.tabId,
        } satisfies YjsSyncMessage);
      } catch {}
      return;
    }
    if (updateBase64.length > BROADCAST_WARN_SIZE) {
      logger.warn('Large broadcast', { size: updateBase64.length });
    }
    const msg: YjsSyncMessage = {
      type: 'yjs-update',
      diagramId: this.diagramId,
      updateBase64,
      seq,
      tabId: this.tabId,
    };
    try {
      this.channel.postMessage(msg);
    } catch (err) {
      logger.warn('Failed to broadcast:', err);
    }
  }

  private handleMessage(event: MessageEvent<YjsSyncMessage>): void {
    const msg = event.data;
    logger.info('📨 Received BroadcastChannel message', { fromTab: msg.tabId, myTab: this.tabId, type: msg.type, seq: msg.seq, diagramId: msg.diagramId });
    if (msg.tabId === this.tabId) return;
    if (msg.diagramId !== this.diagramId) return;

    // M4: Handle resync signal — other tab had an oversized update
    if (msg.type === 'yjs-resync-needed') {
      logger.warn('Remote tab signaled resync needed');
      this.options.onResyncNeeded?.();
      return;
    }

    if (msg.type !== 'yjs-update') return;

    try {
      const update = base64ToUint8Array(msg.updateBase64);
      const shapesBefore = this.doc.getMap('shapes').size;
      Y.applyUpdate(this.doc, update, 'broadcast');
      const shapesAfter = this.doc.getMap('shapes').size;
      logger.info('✅ Applied broadcast update', { seq: msg.seq, shapesBefore, shapesAfter, updateSize: update.byteLength });

      // M5: Propagate remote tab's confirmed seq so our latestConfirmedSeq stays current
      if (msg.seq !== null && this.options.onRemoteSeq) {
        this.options.onRemoteSeq(msg.seq);
      }
    } catch (err) {
      logger.warn('Failed to apply broadcast update:', err);
    }
  }

  destroy(): void {
    if (this.channel) {
      this.channel.close();
      this.channel = null;
    }
  }
}
